import AuthCourseSupport from "../../components/Auth/AuthCourseSupport";
import { coursesEnabled } from "../../config/courseFeatures";
import React, { useEffect, useState } from "react";
import { FaHouse } from "react-icons/fa6";
import { ClipLoader } from "react-spinners";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import useForm from "../../hooks/useForm";
import LoginInput from "../../components/Form/LoginInput";
import LoginButton from "../../components/Form/LoginButton";
import facebookIcon from "../../assets/Icons/facebook.svg";
import logo from "../../assets/Sidebar/Dark_logo.png";
import googleIcon from "../../assets/Icons/google.svg";
import appleIcon from "../../assets/Icons/apple.svg";
import blueBg from "../../assets/Login/bluebg.svg";
import showToast from "../../utils/toastNotifications";
import { getGoogleAuthUrl } from "../../utils/googleAuth";
import { USER_INPUT_FIELD_TYPES } from "../../constants/userInputFieldNames";
import { errorMessages } from "../../constants/frontendErrorMessages";
import { useThunk } from "../../hooks/useThunk";
import { signin, setUser } from "../../store";

const SignIn = () => {
  const dispatch = useDispatch();

  const [showPassword, setShowPassword] = useState(false);
  const [inlineMessage, setInlineMessage] = useState("");
  const [doSignin, isSigninIn, signinError] = useThunk(signin);
  const navigate = useNavigate();

  const {
    formData,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    validateForm,
    setTouched,
  } = useForm(
    {
      email: "",
      password: "",
      // email: "chamalkaauth@freesourcecodes.com",
      // password: "marasinghe@gmail.com"
    },
    {
      email: true,
      password: true,
    },
    {
      email: USER_INPUT_FIELD_TYPES.EMAIL,
      password: USER_INPUT_FIELD_TYPES.PASSWORD,
    }
  );

  useEffect(() => {
    const title = "Dashboard";
    const description =
      "Sign in to CoursePivot to access your order dashboard and get to interact with the best US and Australia-based professional academic writers";
    const previousTitle = document.title;
    const descriptionTag = document.querySelector('meta[name="description"]');
    const previousDescription = descriptionTag?.getAttribute("content");

    document.title = title;
    descriptionTag?.setAttribute("content", description);

    return () => {
      document.title = previousTitle;
      if (descriptionTag && previousDescription) {
        descriptionTag.setAttribute("content", previousDescription);
      }
    };
  }, []);

  useEffect(() => {
    if (!inlineMessage) return;

    const timeoutId = setTimeout(() => {
      setInlineMessage("");
    }, 5000);

    return () => clearTimeout(timeoutId);
  }, [inlineMessage]);

  const toSentenceCase = (message = "") => {
    const trimmedMessage = `${message || ""}`.trim().toLowerCase();
    if (!trimmedMessage) return "";

    return trimmedMessage.replace(/(^\s*\w|[.!?]\s*\w)/g, (match) => match.toUpperCase());
  };

  const getSignInErrorMessage = (message = "") => {
    const normalizedMessage = message.toLowerCase();

    if (normalizedMessage.includes("user") && normalizedMessage.includes("not found")) {
      return "We could not find an account with that email address.";
    }

    if (normalizedMessage.includes("incorrect") && normalizedMessage.includes("password")) {
      return "Incorrect password.";
    }

    return toSentenceCase(message) || "Unable to sign in. Please try again.";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setInlineMessage("");

    // Mark all fields as touched
    setTouched({
      email: true,
      password: true,
    });

    if (validateForm()) {
      const result = await doSignin(formData);
      if (result?.success) {
        const otpSent = result.response?.data.OTP;

        // navigate to OTP page if OTP verification is Needed (OTP skip for thrusted Devices)
        if (otpSent) {
          showToast("success", "Otp sent to your email successfully.");
          navigate("/otp", { state: { otpAvailable: true } });
        } else {
          // set token
          localStorage.setItem("token", result.response.data.token);
          dispatch(setUser(result.response.data.userData));
          showToast("success", "Login successful.");
          navigate("/"); // navigate to dashbord
        }
      } else {
        const errorMessage = getSignInErrorMessage(result.error?.message);
        setInlineMessage(errorMessage);
        showToast("error", errorMessage);
      }
    }
  };

  // redirect user to google concent screen
  const handleGoogleSignin = (e) => {
    e.preventDefault();
    const authUrl = getGoogleAuthUrl();
    if (!authUrl) {
      showToast("error", errorMessages.FAILED_GOOGLE_SIGNIN);
      return;
    }
    window.location.href = authUrl;
  };

  return (
    <div className="auth-page-shell auth-page-signin flex flex-col min-w-0 relative w-full overflow-x-hidden">
      {/* WatermarkBg*/}

      <div className="absolute z-0 top-0 right-0 w-full login-br:block">
        <img
          src={blueBg}
          alt="background"
          className="hidden lg:block absolute -right-15 -top-20 lg:right-0 w-full  lg:max-w-[750px] xl:max-w-[750px] 2xl:max-w-[800px] 1920-br:max-w-[850px] 2560-br:max-w-[1400px] transition-all duration-300"
        />
      </div>

      {/* Form Container-left  */}
      <div className="auth-content-shell relative z-10 mx-auto mb-0 flex flex-col lg:flex-row flex-grow items-start justify-center login-br:justify-between w-full 2xl:max-w-[1536px] auth-layout-structure-in-tab-screen-higher-than-880-height">
        <div className="auth-panel-primary w-full lg:w-[60%] xl:w-[55%] 2xl:w-[50%] flex flex-col justify-center px-4 lg:px-4 ml-0 bg-transparent rounded-[32px] 1920-br:translate-x-[80px]">
          <div className="auth-card w-full bg-white rounded-[32px] p-6 lg:p-8 border border-solid border-[color:lightskyblue] shadow-none lg:shadow-[0_20px_35px_rgba(15,23,42,0.06)]">
            <div className="auth-card-header flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex flex-col sm:flex-row-reverse sm:justify-end">
                <img
                  src={logo}
                  alt="CoursePivot"
                  className="sm:ml-[12px] mb-5 sm:mb-0 px-[8px] h-[40px] w-[150px] sm:w-auto object-contain"
                />
                <h1 className="text-level-1 font-roboto font-bold leading-[44px] text-primary mb-2 sm:mb-4">
                  Sign In
                </h1>
              </div>
              <a
                href="https://coursepivot.com"
                className="inline-flex items-center gap-2 self-start rounded-lg border border-medium-gray px-3 py-2 text-sm font-semibold text-primary transition-colors hover:border-primary hover:bg-soft-blue"
              >
                <FaHouse className="h-4 w-4" />
                Home
              </a>
            </div>
          <p className="auth-intro mb-0 sm:mb-10 font-inter text-[18px] text-gray w-full max-w-none sm:max-w-[200px] md:max-w-[220px] lg:max-w-[260px] xl:max-w-none">
            Please login to continue to your account.
          </p>

          <form className="auth-form w-full max-w-full max-sm:mt-6">
            <div>
              <div className="auth-fields grid grid-cols-1 md:gap-1 lg:mr-6 xl:mr-36 ">
                <LoginInput
                  className=" w-full 2xl:w-full px-4 "
                  id="email"
                  name="email"
                  label="Email Address"
                  type="email"
                  value={formData.email}
                  placeholder="Enter your email address"
                  error={touched.email && errors.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  onBlur={() => handleBlur("email", formData.email)}
                />
                <LoginInput
                  className="relative w-full lg:w-full lg:mr-6 xl:mr-36 2xl:w-full px-4"
                  position=""
                  id="password"
                  name="password"
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  placeholder="Enter your password"
                  error={touched.password && errors.password}
                  showPasswordToggle
                  isPasswordVisible={showPassword}
                  togglePasswordVisibility={() =>
                    setShowPassword(!showPassword)
                  }
                  onChange={(e) =>
                    handleInputChange("password", e.target.value)
                  }
                  onBlur={() => handleBlur("password", formData.password)}
                />
              </div>
              {inlineMessage && (
                <p className="mt-2 px-4 font-inter text-[13px] font-medium leading-5 text-light-red lg:mr-6 xl:mr-36">
                  {inlineMessage}
                </p>
              )}
            </div>

            <div className="auth-options flex items-center justify-between mt-2 lg:mr-6 xl:mr-36 ">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="ml-1 accent-blue-shades border-gray-300 rounded"
                />
                <span className="ml-2 font-inter text-[15px] leading-[18.75px] text-gray">
                  Remember me
                </span>
              </label>

              <a
                href="/forgot-password"
                className="font-lato text-[15px] leading-[18.75px] text-blue-shades hover:underline"
              >
                Forgot Your Password?
              </a>
            </div>

            <div className="auth-actions flex flex-col items-center justify-center mt-14 lg:mr-6 xl:mr-36 ">
              {/* Sign In Button */}
              <LoginButton
                text={
                  isSigninIn ? (
                    <ClipLoader
                      loading={isSigninIn}
                      speedMultiplier={1}
                      size={22}
                      color="#60a5fa"
                    />
                  ) : (
                    "Sign In"
                  )
                }
                type="submit"
                className="w-full py-3 h-[54px]"
                onClick={handleSubmit}
                disabled={isSigninIn}
              />

              {/* Sign Up Link */}
              <p className="mt-3 font-lato text-[15px] text-center leading-[18.75px] text-black/50">
                Don’t have an account?{" "}
                <a href="/signup" className="text-blue-shades hover:underline">
                  Sign Up
                </a>
              </p>

              {/* social logins */}
              <div className="auth-social flex mt-6 w-full ">
                <div className="w-full ">
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-medium-gray"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 font-inter font-medium text-level-3 leading-[24px] text-gray bg-white">
                        or
                      </span>
                    </div>
                  </div>

                  <div className="auth-google-row grid grid-cols-1 h-[54px] gap-3 mt-4">
                    {/* <button
                      className="flex items-center justify-center px-4 py-2 border rounded-md border-medium-gray hover:bg-gray-50"
                      disabled={isSigninIn}
                      type="button"
                    >
                      <img
                        src={facebookIcon}
                        alt="Facebook"
                        className="w-5 h-5"
                      />
                    </button> */}
                    <button
                      className="flex items-center justify-center gap-3 font-inter font-semibold text-[18px] leading-[21.6px] text-gray py-3 rounded-lg hover:bg-[#EFEFEF] transition duration-200 px-4 py-2 border rounded-md border-medium-gray "
                      onClick={handleGoogleSignin}
                      disabled={isSigninIn}
                    >
                      <img src={googleIcon} alt="Google" className="w-5 h-5" />
                      <span>Continue with Google</span>
                    </button>
                    {/* <button
                      className="flex items-center justify-center px-4 py-2 border rounded-md border-medium-gray hover:bg-gray-50"
                      disabled={isSigninIn}
                      type="button"
                    >
                      <img src={appleIcon} alt="Apple" className="w-5 h-5" />
                    </button> */}
                  </div>
                </div>
              </div>
            </div>
          </form>
          </div>
        </div>

        {/* Course support and order benefits */}
        <div className="auth-benefits-column flex flex-col justify-start w-full max-w-[520px] mt-10 lg:mt-0 px-4 bg-transparent 1920-br:-translate-x-[80px]">
          {coursesEnabled ? <AuthCourseSupport brand="coursepivot" /> : (
          <div className="auth-benefits-card w-full rounded-[24px] border border-slate-200 bg-white p-6">
            <div className="space-y-2">
              <h2 className="text-[22px] font-semibold text-[#0f172a]">
                Get these premium benefits for free
              </h2>
              <p className="text-sm text-slate-500">
                Everything you need to order with confidence.
              </p>
            </div>

            <div className="auth-benefits-list grid gap-3">
              <div className="flex items-start gap-3 rounded-2xl border border-[#dbeafe] bg-[#f8fbff] p-3">
                <span className="mt-1 inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-[#eff6ff] text-[#2563eb] text-base font-bold">
                  ✓
                </span>
                <div>
                  <p className="font-semibold text-[#111827]">Free Turnitin AI report</p>
                  <p className="text-[13px] text-[#475569]">Turnitin report to verify no AI was used, your paper won't be stored in Turnitin's database.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-2xl border border-[#dbeafe] bg-[#f8fbff] p-3">
                <span className="mt-1 inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-[#eff6ff] text-[#2563eb] text-base font-bold">
                  ✓
                </span>
                <div>
                  <p className="font-semibold text-[#111827]">Free Similarity Report</p>
                  <p className="text-[13px] text-[#475569]">Turnitin report to verify your paper has no plagiarism, paper won't be stored in Turnitin's database.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-2xl border border-[#dbeafe] bg-[#f8fbff] p-3">
                <span className="mt-1 inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-[#eff6ff] text-[#2563eb] text-base font-bold">
                  ✓
                </span>
                <div>
                  <p className="font-semibold text-[#111827]">Free Unlimited Revisions</p>
                  <p className="text-[13px] text-[#475569]">You can ask for free revisions until you get satisfied.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-2xl border border-[#dbeafe] bg-[#f8fbff] p-3">
                <span className="mt-1 inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-[#eff6ff] text-[#2563eb] text-base font-bold">
                  ✓
                </span>
                <div>
                  <p className="font-semibold text-[#111827]">On-time Delivery</p>
                  <p className="text-[13px] text-[#475569]">Deadlines are tracked for every order.</p>
                </div>
              </div>
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignIn;
